import React from "react";
import { Link } from "react-router-dom";
import {
  FlaskConical, Wrench, ClipboardCheck, Calculator, Server, Layers,
  Beaker, Target, Palette, FileSignature, Brain, TrendingUp, ArrowRight, Sparkles,
} from "lucide-react";

const MODULES = [
  { slug: "copilot",           title: "AI Technical Sales Copilot", desc: "Diagnose print defects. Recommend formulations. Generate customer reports in seconds.", icon: Sparkles,      href: "/app/ink/copilot",     live: true, hero: true },
  { slug: "products",          title: "Product Knowledge Center",   desc: "Ink specs, resin database, pigment library, additives, SDS/TDS, VOC, FDA compliance.",    icon: FlaskConical,  href: "/app/ink/products",    live: true },
  { slug: "troubleshoot",      title: "Print Troubleshooting AI",   desc: "9 defects · root causes · corrective actions · queryable by press and substrate.",       icon: Wrench,        href: "/app/ink/troubleshoot", live: true },
  { slug: "presses",           title: "Press & Equipment Database", desc: "Flexo · Offset · Gravure · Digital · Corrugated. Track configurations and recommended inks.", icon: Server,     href: "/app/ink/presses",     live: true },
  { slug: "reports",           title: "Saved Customer Reports",     desc: "Every Copilot analysis you save. Reopen, edit, export, or resend to the customer.",       icon: FileSignature, href: "/app/ink/reports",     live: true },
  { slug: "visit",             title: "Customer Visit Assistant",   desc: "Meeting notes, machine info, defect photos, follow-up reports, action items, sample deliveries.", icon: ClipboardCheck, href: "/app/ink/coming-soon/visit", live: false },
  { slug: "cost",              title: "Ink Cost Calculator",        desc: "Coverage · CPMi · BCM optimization · anilox tuning · ROI comparisons.",                   icon: Calculator,    href: "/app/ink/coming-soon/cost", live: false },
  { slug: "application",       title: "Application Recommendation", desc: "Film · Paper · Foil · Corrugated · Shrink sleeve — recommends the correct ink technology.", icon: Layers,       href: "/app/ink/coming-soon/application", live: false },
  { slug: "samples",           title: "Sample & Trial Manager",     desc: "Sample requests, lab batches, trial tracking, customer approvals, production validation.", icon: Beaker,      href: "/app/ink/coming-soon/samples", live: false },
  { slug: "competitive",       title: "Competitive Intelligence Hub", desc: "Competitor products, strengths, weaknesses, pricing notes, battle cards.",              icon: Target,        href: "/app/ink/coming-soon/competitive", live: false },
  { slug: "color",             title: "Color Management Suite",     desc: "Pantone lookup · Delta E · spectrophotometer integration · drawdown history.",           icon: Palette,       href: "/app/ink/coming-soon/color", live: false },
  { slug: "proposals",         title: "Sales Proposal Generator",   desc: "Technical proposals · trial reports · cost savings summaries · customer presentations.",   icon: FileSignature, href: "/app/ink/coming-soon/proposals", live: false },
  { slug: "process",           title: "Manufacturing Process Analyzer", desc: "'Why is this mottling?' · 'How do I improve opacity?' — structured AI recommendations.", icon: Brain,       href: "/app/ink/coming-soon/process", live: false },
  { slug: "accounts",          title: "Account Intelligence Dashboard", desc: "Installed equipment, current ink systems, annual usage, opportunities, pain points, revenue.", icon: TrendingUp, href: "/app/ink/coming-soon/accounts", live: false },
];

export default function InkHub() {
  const live = MODULES.filter(m => m.live);
  const soon = MODULES.filter(m => !m.live);
  const hero = MODULES.find(m => m.hero);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
      <div className="mb-10">
        <div className="overline text-accent">/ INDUSTRY MODULE</div>
        <h1 className="font-display font-black text-4xl md:text-5xl tracking-tight mt-3" data-testid="ink-hub-title">
          Ink &amp; Print
        </h1>
        <p className="text-muted-foreground mt-3 max-w-2xl">
          Built for technical sales, application specialists, and print-shop operators.
          Diagnose defects, formulate recommendations, and turn field visits into closed deals.
        </p>
      </div>

      {/* Hero: Copilot */}
      {hero && (
        <Link to={hero.href} className="block group" data-testid="ink-hero-copilot">
          <div className="relative overflow-hidden border border-border rounded-sm bg-zinc-950 text-white p-8 lg:p-10 mb-10 hover:border-accent transition-colors">
            <div className="absolute top-0 right-0 w-96 h-96 bg-accent/10 rounded-full blur-3xl pointer-events-none" />
            <div className="relative flex flex-col lg:flex-row lg:items-center gap-6">
              <div className="flex-1">
                <div className="inline-flex items-center gap-2 border border-accent/40 bg-accent/10 rounded-sm px-3 py-1.5 mb-4">
                  <Sparkles className="w-3.5 h-3.5 text-accent" />
                  <span className="text-[10px] font-mono-plex uppercase tracking-widest text-accent">◉ Star feature · Live</span>
                </div>
                <div className="font-display font-black text-3xl lg:text-4xl tracking-tight">
                  AI Technical Sales Copilot
                </div>
                <p className="text-zinc-400 mt-3 max-w-2xl">
                  Feed it a defect, a press, a substrate, and an anilox spec. In under 30 seconds you&rsquo;ll have a full technical analysis:
                  ranked root causes, questions to ask, ink formulation, additives, press adjustments, cost impact — and a customer-ready
                  Markdown report you can save or send.
                </p>
              </div>
              <div className="shrink-0 border border-zinc-800 bg-zinc-900 rounded-sm px-6 py-4 text-center">
                <div className="text-xs font-mono-plex uppercase tracking-widest text-zinc-500">Try it now</div>
                <div className="font-display font-bold text-2xl text-accent mt-1">Analyze a case →</div>
              </div>
            </div>
          </div>
        </Link>
      )}

      {/* Live modules */}
      <div className="mb-6 overline text-muted-foreground">/ LIVE NOW</div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
        {live.filter(m => !m.hero).map((m) => {
          const Icon = m.icon;
          return (
            <Link key={m.slug} to={m.href} className="group" data-testid={`ink-tile-${m.slug}`}>
              <div className="h-full border border-border bg-card p-6 rounded-sm hover:border-primary hover:shadow-lg transition-all">
                <div className="flex items-start justify-between gap-3">
                  <div className="w-10 h-10 rounded-sm bg-primary/10 grid place-items-center">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                </div>
                <div className="font-display font-bold text-lg mt-4">{m.title}</div>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{m.desc}</p>
              </div>
            </Link>
          );
        })}
      </div>

      {/* Coming soon */}
      <div className="mb-6 overline text-muted-foreground">/ SHIPPING NEXT</div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {soon.map((m) => {
          const Icon = m.icon;
          return (
            <Link key={m.slug} to={m.href} className="group" data-testid={`ink-tile-${m.slug}`}>
              <div className="h-full border border-dashed border-border bg-card/30 p-6 rounded-sm hover:border-primary/60 transition-all">
                <div className="flex items-start justify-between gap-3">
                  <div className="w-10 h-10 rounded-sm bg-muted grid place-items-center">
                    <Icon className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <span className="text-[10px] font-mono-plex uppercase tracking-widest text-muted-foreground">Soon</span>
                </div>
                <div className="font-display font-bold text-lg mt-4">{m.title}</div>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{m.desc}</p>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
